import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { isAndroid, isIOS, isMobile } from 'react-device-detect';

import postDownloadImage from 'theSource/assets/postDownloadImage.svg';
import { isInStandaloneMode } from 'theSource/utils';
import {
  useConfigurationQuery,
  useContentSettingsQuery,
} from 'theSource/queries';
import {
  CancelButtonStyled,
  DialogActionsStyled,
  DialogContentBelowImageStyled,
  DialogImageStyled,
  DialogStyled,
  DialogTitleStyled,
  LogoStyled,
  DownloadInstructionsTypographyStyled,
} from './PostLoginDownloadPromptStyled';
import { Button, Spacer } from 'theSource/components';
import { selectNativeAuth } from 'theSource/services';
import { selectIsAuthenticated } from 'theSource/features';
import {
  POST_LOGIN_DOWNLOAD_PROMPT_KEY,
  PWA_INSTALLED_KEY,
  PENDO_EVENT_DOWNLOAD,
  userOnIOSNotSafari,
  userOnIOSSafari,
  getInstalledValue,
  getPromptShownCount,
  PROMPT_SHOW_MAX,
  getDeviceType,
} from './utils';
import { selectDownloadPrompt } from './services';

declare let pendo: any;

export const PostLoginDownloadPrompt = () => {
  const { t } = useTranslation('messages.downloadPrompt');

  const { isSupported, isRegistered, isEnabled } =
    useSelector(selectNativeAuth);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { configuration } = useConfigurationQuery();
  const { contentSettings } = useContentSettingsQuery({
    enabled: Boolean(isAuthenticated && configuration?.apiHost),
  });
  const downloadPrompt = useSelector(selectDownloadPrompt);

  const isNativeAuthDialogOpened =
    isEnabled &&
    isSupported &&
    !isRegistered &&
    contentSettings?.user_name !== (null || undefined);

  const promptShownCount = getPromptShownCount();
  const isInstalled = getInstalledValue();

  const [stayClosed, setStayClosed] = useState(isInstalled !== null);
  const [isOpened, setIsOpened] = useState(
    !isNativeAuthDialogOpened &&
      isInstalled === null &&
      Number(promptShownCount) < PROMPT_SHOW_MAX &&
      isMobile &&
      !isInStandaloneMode()
  );

  const handleDownloadTracking = () => {
    const time = new Date();
    localStorage.setItem(PWA_INSTALLED_KEY, 'installed');
    pendo.track(PENDO_EVENT_DOWNLOAD, {
      install_at: time,
      device: getDeviceType(),
    });
  };

  const handleCancelClick = () => {
    setStayClosed(true);
    localStorage.setItem(
      POST_LOGIN_DOWNLOAD_PROMPT_KEY,
      (Number(promptShownCount) + 1).toString()
    );
  };

  const handleAndroidInstall = () => {
    if (downloadPrompt === undefined) {
      return;
    }
    downloadPrompt.prompt();
    downloadPrompt.userChoice.then((userChoice) => {
      if (userChoice.outcome === 'accepted') {
        handleDownloadTracking();
      } else {
        localStorage.setItem(
          POST_LOGIN_DOWNLOAD_PROMPT_KEY,
          (Number(promptShownCount) + 1).toString()
        );
      }
      setStayClosed(true);
    });
  };

  useEffect(() => {
    if (isInstalled !== null) {
      setStayClosed(true);
    }
  }, [isInstalled]);

  useEffect(() => {
    if (isInStandaloneMode() && isInstalled === null) {
      handleDownloadTracking();
      setStayClosed(true);
    }
  }, [isOpened, isInstalled]);

  // this is used to open/close the prompt according the the nativeauthdialog opened state
  // but we still need to check for other criteria
  useEffect(() => {
    if (
      !isNativeAuthDialogOpened &&
      !stayClosed &&
      Number(promptShownCount) < PROMPT_SHOW_MAX &&
      isInstalled === null &&
      isMobile
    ) {
      setIsOpened(true);
    } else {
      setIsOpened(false);
    }
  }, [
    isNativeAuthDialogOpened,
    stayClosed,
    promptShownCount,
    isOpened,
    isInstalled,
  ]);

  return (
    <DialogStyled data-testid="download-dialog" open={isOpened} fullScreen>
      <DialogActionsStyled>
        {isIOS ? (
          <>
            <CloseIcon color="info" />
            <CancelButtonStyled
              data-testid="cancel-button-ios"
              onClick={handleCancelClick}
              label={t('common:cancel')}
              variant="tertiary"
              fullWidth={false}
            />
          </>
        ) : (
          <></>
        )}
      </DialogActionsStyled>
      <DialogImageStyled>
        <LogoStyled src={postDownloadImage} alt="download_graphic" />
        <Spacer height={8} />
        <DialogTitleStyled variant="h5">
          {t('betterExperienceTitle')}
        </DialogTitleStyled>
        {userOnIOSNotSafari ? (
          <DialogTitleStyled variant="body1" data-testid="ios-not-safari-body">
            {t('downloadIOSNotSafariMessage')}
          </DialogTitleStyled>
        ) : (
          <DialogTitleStyled variant="body1" data-testid="generic-body">
            {t('downloadGenericMessage')}
          </DialogTitleStyled>
        )}
        <Spacer height={8} />
      </DialogImageStyled>

      {isAndroid ? (
        // user is on Android device, browser choice does not matter for downloading PWA
        <DialogContentBelowImageStyled>
          <Spacer height={8} />
          <Button
            data-testid="cancel-button-android"
            onClick={handleCancelClick}
            label={t('dismissAndroidButton')}
            variant="tertiary"
            fullWidth={false}
            style={{ paddingRight: 20 }}
          />
          <Button
            data-testid="download-button"
            onClick={handleAndroidInstall}
            label={t('downloadAndroid')}
            variant="primary"
            fullWidth={false}
            style={{ paddingLeft: 20 }}
          />
          <Spacer height={8} />
        </DialogContentBelowImageStyled>
      ) : userOnIOSSafari ? (
        // user is on IOS and using safari browser
        <DialogContentBelowImageStyled data-testid="safari-ios-download-instruction">
          <Spacer height={8} />
          <DownloadInstructionsTypographyStyled variant="caption">
            <Trans
              components={{
                ExportIcon: <IosShareIcon />,
                AddIcon: <AddBoxOutlinedIcon />,
              }}
            >
              {t('downloadIOSSafariMessage')}
            </Trans>
          </DownloadInstructionsTypographyStyled>
          <Spacer height={8} />
        </DialogContentBelowImageStyled>
      ) : (
        // assumes user is on IOS but not safari browser
        <></>
      )}
    </DialogStyled>
  );
};
